import { memo, useEffect, useState } from "react";
import { ReactSVG } from "react-svg";
import { stripHtml } from "helpers";
import {
  NotificationScope,
  EntityItem,
} from "services/api/notifications/types";


import {
  ENTITY_TYPE_LABELS,
  ENTITY_TYPE_ICONS,
  CHANNEL_OPTIONS,
  getScopeOptions,
} from "services/api/notifications/consts";
import { getUsersAPI } from "services/api/users/api";
import { getEntityItemsAPI } from "services/api/notifications/api";
import { Pagination } from "services/search/types";
import { PaginationDirection } from "services/url";
import { StepProps } from "../types";
import Badge from "ui/components/Badge/Badge";
import { BadgeProps } from "ui/components/Badge/types";

const usersPagination: Pagination = {
  pageNumber: 1,
  pageSize: 500,
  sort: { sortBy: "firstName", direction: PaginationDirection.Ascending },
};

const Step5Summary: React.FC<StepProps> = ({ formValues }) => {
  const [userMap, setUserMap] = useState<Record<string, string>>({});
  const [entityMap, setEntityMap] = useState<Record<string, string>>({});

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (formValues.entityType && formValues.scope === NotificationScope.Individual) {
      fetchEntityItems(formValues.entityType);
    }
  }, [formValues.entityType, formValues.scope]);

  const fetchUsers = async () => {
    try {
      const result = await getUsersAPI(usersPagination);
      const map: Record<string, string> = {};
      result.data.forEach((u) => { map[u.id] = `${u.firstName} ${u.lastName}`; });
      setUserMap(map);
    } catch { /* handled by createApiCall */ }
  };

  const fetchEntityItems = async (entityType: number) => {
    try {
      const items = await getEntityItemsAPI(entityType);
      const map: Record<string, string> = {};
      items.forEach((item: EntityItem) => { map[item.id] = item.displayName; });
      setEntityMap(map);
    } catch { /* handled by createApiCall */ }
  };

  const resolveNames = (ids: string[], lookup: Record<string, string>) =>
    ids.map((id) => lookup[id]).filter(Boolean);

  const entityLabel =
    formValues.entityType != null
      ? ENTITY_TYPE_LABELS[formValues.entityType]
      : "—";

  const entityIcon =
    formValues.entityType != null
      ? ENTITY_TYPE_ICONS[formValues.entityType]
      : null;

  const scopeLabel =
    getScopeOptions("").find((o) => o.value === formValues.scope)?.label ?? "—";

  const activeChannels = CHANNEL_OPTIONS.filter(
    (ch) => formValues.channels & ch.value
  );

  return (
    <div className="flex justify-center h-full px-4 mt-[100px]">
      <div className="w-full max-w-4xl">
        <div className="flex flex-col gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 text-center mb-1">
              Pregled notifikacije
            </h1>
            <p className="text-center text-sm text-slate-600 mb-6 max-w-2xl mx-auto ">
              Provjerite postavke prije spremanja
            </p>
          </div>

          {/* OSNOVNO */}
          <div className="border border-dashed bg-white border-blue-300 pt-3  mb-3 rounded-sm">
            <div className="text-md font-medium text-slate-900  mb-1.5 px-3">Osnovne informacije:</div>


            <div className="flex items-center justify-between border-b border-dashed border-blue-300 px-4 py-3">
              <div className="text-sm text-slate-500">Tip </div>
              <div className="flex items-center gap-2">
                {entityIcon && (
                  <ReactSVG
                    src={entityIcon}
                    className="w-4 h-4 [&_svg]:stroke-current text-slate-600"
                  />
                )}
                <span className="text-sm font-medium text-slate-900">
                  {entityLabel}
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between border-b border-dashed border-blue-300 px-4 py-3">
              <div className="text-sm text-slate-500">Naziv</div>
              <div className="text-sm font-medium text-slate-900">
                {formValues.title || "—"}
              </div>
            </div>

            <div className="flex items-start justify-between border-b border-dashed border-blue-300 px-4 py-3">
              <div className="text-sm text-slate-500 shrink-0">Predložak poruke</div>
              <div className="text-sm font-medium text-slate-900 text-right max-w-[60%] line-clamp-3">
                {stripHtml(formValues.messageTemplate) || "—"}
              </div>
            </div>

            {!formValues.isCustom && (
              <div className="flex items-center justify-between border-b border-dashed border-blue-300 px-4 py-3">
                <div className="text-sm text-slate-500">Opseg</div>
                <div className="text-sm font-medium text-slate-900">
                  {scopeLabel}
                </div>
              </div>
            )}

            {formValues.isCustom && (
              <div className="flex items-center justify-between border-b border-dashed border-blue-300 px-4 py-3">
                <div className="text-sm text-slate-500">Datum slanja</div>
                <div className="text-sm font-medium text-slate-900">
                  {formValues.customScheduledAt
                    ? new Date(formValues.customScheduledAt).toLocaleDateString("hr-HR")
                    : "—"}
                </div>
              </div>
            )}

            <div className="flex items-center justify-between px-4 py-3">
              <div className="text-sm text-slate-500">Kanali</div>
              <div className="flex items-center gap-1">
                {activeChannels.length > 0
                  ? activeChannels.map((ch) => (
                    <Badge
                      key={ch.value}
                      text={ch.label}
                      variant={ch.variant}
                      height="small"
                      icon={ch.icon}
                    />
                  ))
                  : <span className="text-sm text-slate-400">—</span>}
              </div>
            </div>
          </div>

          {/* PRIMATELJI */}
          <div className="border border-dashed bg-white border-blue-300 pt-3  mb-3 rounded-sm">
            <div className="text-md font-medium text-slate-900  mb-1.5 px-3">Primatelji:</div>

            <SummaryListRow
              label="Primatelji"
              names={resolveNames(formValues.includedUserIds, userMap)}
              borderBottom={formValues.scope === NotificationScope.Individual && !!formValues.entityType}
              badgeVariant="success"
            />

            {formValues.scope === NotificationScope.Individual && formValues.entityType && (
              <SummaryListRow
                label="Odabrani entiteti"
                names={resolveNames(formValues.includedEntityIds, entityMap)}
              />
            )}
          </div>

          {/* PRAVILA — only for non-custom */}
          {!formValues.isCustom && (
            <div className="border border-dashed bg-white border-blue-300 pt-3  mb-3 rounded-sm">
              <div className="text-md font-medium text-slate-900  mb-1.5 px-3">Pravila slanja:</div>
              <div className="flex items-center justify-between border-b border-dashed border-blue-300 px-4 py-3">
                <div className="text-sm text-slate-500">Dana prije isteka</div>
                <div className="text-sm font-medium text-slate-900">
                  {formValues.daysBeforeExpiry != null
                    ? `${formValues.daysBeforeExpiry} dana`
                    : "—"}
                </div>
              </div>

              <div className="flex items-center justify-between border-b border-dashed border-blue-300 px-4 py-3">
                <div className="text-sm text-slate-500">Obavijest bez datuma isteka</div>
                {formValues.notifyIfDateMissing ? (
                  <Badge text="Da" variant="success" height="small" icon="/media/icons/check.svg" />
                ) : (
                  <Badge text="Ne" height="small" />
                )}
              </div>

              {formValues.isRepeating && (
                <div className="flex items-center justify-between px-4 py-3">
                  <div className="text-sm text-slate-500">Ponavljanje</div>
                  <div className="text-sm font-medium text-slate-900">
                    Svakih {formValues.repeatIntervalDays ?? "—"} dana, {formValues.repeatCount ?? "—"}x
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const SummaryListRow: React.FC<{
  label: string;
  names: string[];
  borderBottom?: boolean;
  badgeVariant?: BadgeProps['variant'];
}> = ({ label, names, borderBottom, badgeVariant = "info" }) => (
  <div
    className={`flex items-center justify-between px-4 py-3 ${borderBottom ? "border-b border-dashed border-blue-300" : ""
      }`}
  >
    <div className="text-sm text-slate-500 shrink-0">{label}</div>
    <div className="flex flex-wrap justify-end gap-1 max-w-[60%]">
      {names.length > 0
        ? names.map((name) => (
          <Badge key={name} text={name} variant={badgeVariant} height="small" />
        ))
        : <span className="text-sm text-slate-400">—</span>}
    </div>
  </div>
);

export default memo(Step5Summary);
